import { Button } from 'react-bootstrap'
import { useCallback, useEffect, useMemo, useState } from 'react'
import Select from 'react-select'

import PageHeading from '../../../elements/v2/PageHeading'
import ErrorToast from '../../../elements/v2/ErrorToast'
import { getBanks } from '../../../../services/PaymentService'
import { useSession } from '../../../../contexts/SessionContext'
import { ReactComponent as CheckCircleSvg } from '../../../../assets/svg/check-circle.svg'

const TERMS_AND_CONDITIONS_URL = 'https://www.mypaga.com/ngn-terms/'
const PRIVACY_POLICY_URL = 'https://www.mypaga.com/ngn-terms/ngn-terms#privacy-policy'

const AccountDetailsScreen = ({
  userAddress,
  setUserAddress,
  accountNumber,
  setAccountNumber,
  selectedBank,
  setSelectedBank,
  accountName,
  setAccountName,
  showOTPVerification,
  showTransferConfirmation,
  otpError,
  setOtpError,
}) => {
  const ACCOUNT_NUMBER_LENGTH = 10

  const { validateAccountDetails, initiateMandate, sessionId, sessionData } = useSession()

  const [loading, setLoading] = useState(false)
  const [isAccountValidating, setIsAccountValidating] = useState(false)
  const [banks, setBanks] = useState(null)
  const [isFetchingBanks, setIsFetchingBanks] = useState(false)
  const isFormValid = Boolean(accountNumber && selectedBank.bankPublicId && accountName && userAddress)

  const isBankError = Boolean(otpError && !accountName && otpError.toLowerCase().includes('bank'))
  const isAccountNumberError = Boolean(otpError && !accountName && !isBankError)
  const errorInputStyle = {
    boxShadow: '0 0 0 4px rgba(253,162,155,0.5)',
    borderColor: '#F04438',
    color: '#F04438',
  }

  const bankOptions = useMemo(
    () => (banks || []).map((bank) => ({ value: bank.id, label: bank.name })),
    [banks]
  )
  const selectedBankOption = bankOptions.find((option) => option.value === selectedBank.bankPublicId) || null

  const bankSelectStyles = {
    control: (base) => ({
      ...base,
      minHeight: '42px',
      height: '42px',
      borderRadius: '0.375rem',
      borderColor: isBankError ? '#F04438' : '#ced4da',
      boxShadow: isBankError ? '0 0 0 4px rgba(253,162,155,0.5)' : 'none',
      textAlign: 'start',
      ':hover': { borderColor: isBankError ? '#F04438' : '#ced4da' },
    }),
    valueContainer: (base) => ({ ...base, height: '42px', padding: '0 12px' }),
    placeholder: (base) => ({ ...base, textAlign: 'start' }),
    input: (base) => ({ ...base, margin: 0, padding: 0, textAlign: 'start', color: isBankError ? '#F04438' : undefined }),
    singleValue: (base) => ({ ...base, textAlign: 'start', color: isBankError ? '#F04438' : base.color }),
    option: (base) => ({ ...base, textAlign: 'start' }),
    indicatorSeparator: () => ({ display: 'none' }),
    indicatorsContainer: (base) => ({ ...base, height: '42px' }),
    menu: (base) => ({ ...base, zIndex: 5 }),
  }

  // Fetch banks on component mount
  useEffect(() => {
    const fetchBanks = async () => {
      setIsFetchingBanks(true)
      try {
        const response = await getBanks()

        if (response.financialInstitutions) {
          setBanks(response.financialInstitutions)
        } else {
          setOtpError(response.message || 'Failed to fetch banks')
        }
      } catch (error) {
        setOtpError(error.statusMessage || 'An error occurred while fetching banks')
      } finally {
        setIsFetchingBanks(false)
      }
    }

    fetchBanks()
  }, [])

  // Auto-validate account when bank is selected and account number is complete
  useEffect(() => {
    const validateAccount = async () => {
      if (
        accountNumber.length === ACCOUNT_NUMBER_LENGTH &&
        selectedBank.bankPublicId &&
        !accountName
      ) {
        setIsAccountValidating(true)
        try {
          const response = await validateAccountDetails(sessionId, {
            bankPublicId: selectedBank.bankPublicId,
            accountNumber: accountNumber,
            referenceNumber: sessionData?.referenceNumber,
          })

          if (response.statusCode === '0') {
            setAccountName(response.data.account_name)
          }
        } catch (error) {
          console.error('Account validation error:', error)
          setOtpError(
              error.statusMessage || 'An error occurred while validating your account details'
          )
        } finally {
          setIsAccountValidating(false)
        }
      }
    }

    validateAccount()
  }, [selectedBank.bankPublicId, accountNumber, accountName, setAccountName, sessionData])

  const handleProceed = useCallback(async () => {
    if (!accountNumber || accountNumber.length < ACCOUNT_NUMBER_LENGTH) {
      setOtpError('Please enter a valid account number')
      return
    }

    if (!selectedBank.bankPublicId) {
      setOtpError('Please select a bank')
      return
    }

    setLoading(true)
    setOtpError('')

    try {
      const response = await initiateMandate(sessionId, {
        address: userAddress,
        bankPublicId: selectedBank.bankPublicId,
        accountNumber,
        accountName,
        referenceNumber: sessionData?.referenceNumber,
        authorizationAmount: sessionData?.metadata?.authorizationAmount,
      })

      showTransferConfirmation(response.data)
    } catch (error) {
      console.error('Error submitting account details:', error)
      setOtpError(
        error.statusMessage || 'An error occurred while submitting account details'
      )
    } finally {
      setLoading(false)
    }
  }, [
    accountNumber,
    selectedBank,
    userAddress,
    accountName,
    sessionId,
    showTransferConfirmation,
    setOtpError,
  ])

  const handleAccountNumberChange = useCallback(
    (e) => {
      const value = e.target.value
      setAccountNumber(value)
      // Always clear so a changed number (even a same-length paste) re-triggers validation
      setAccountName('')
      setOtpError('')
    },
    [setAccountNumber, setAccountName, setOtpError]
  )

  return (
    <div
      style={{ height: '100%', padding: 'var(--dd-content-padding-y) var(--dd-content-padding-x)' }}
      className='w-100 d-flex flex-column justify-content-between'
    >
      <div style={{ position: 'relative' }}>
      {accountName && (
        <div
          className='rounded d-flex align-items-center gap-2'
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            background: '#EBF6EB',
            padding: '12px 8px',
            boxShadow: '0px 4px 8px 0px #1018281A, 0px 2px 4px 0px #1018280F',
          }}
        >
          <CheckCircleSvg width={16} height={16} />
          <p className='small fw-medium mb-0' style={{ color: '#376E37' }}>
            Validation successful
          </p>
        </div>
      )}
      <PageHeading
        className={accountName ? 'mb-4 mt-5' : 'my-4'}
        onBackButtonClicked={() => showOTPVerification()}
      />
      <form>
        <div>
          <label
            htmlFor='userAddress'
            className='d-block text-start fw-medium small mb-2'
          >
            Address<span style={{ color: '#cf0000' }}>*</span>
          </label>
          <input
            type='text'
            style={{ height: '42px' }}
            className='form-control'
            id='userAddress'
            value={userAddress}
            onChange={(e) => setUserAddress(e.target.value)}
            placeholder='Enter your address. E.g Plot 5 Montgomery Road, Herbert Macaulay way. Yaba'
          />
        </div>

        <div className='my-3 small'>
          <label
            htmlFor='userAccountNumber'
            className='d-block text-start fw-medium small mb-2'
          >
            Account number<span style={{ color: '#cf0000' }}>*</span>
          </label>
          <input
            type='text'
            style={{
              height: '42px',
              ...(isAccountNumberError ? errorInputStyle : {}),
            }}
            className='form-control'
            id='userAccountNumber'
            value={accountNumber}
            onChange={handleAccountNumberChange}
            disabled={isAccountValidating}
            maxLength={ACCOUNT_NUMBER_LENGTH}
          />
          {isAccountValidating && (
            <div className='small text-muted mt-1'>Validating account...</div>
          )}
        </div>

        <div className='my-3 small'>
          <label
            htmlFor='userBankSelection'
            className='d-block text-start fw-medium small mb-2'
          >
            Bank<span style={{ color: '#cf0000' }}>*</span>
          </label>
          <Select
            inputId='userBankSelection'
            classNamePrefix='bank-select'
            styles={bankSelectStyles}
            options={bankOptions}
            value={selectedBankOption}
            placeholder={isFetchingBanks ? 'Loading banks...' : 'Select Bank'}
            isLoading={isFetchingBanks}
            isDisabled={isFetchingBanks}
            isClearable
            noOptionsMessage={() => 'No matching bank'}
            onChange={(option) => {
              setSelectedBank({
                bankPublicId: option ? option.value : '',
                bankName: option ? option.label : '',
              })
              setAccountName('')
              setOtpError('')
            }}
          />
        </div>
      </form>

      {accountName && (
        <div
          className='rounded py-2 px-3 fw-medium d-flex align-items-center justify-content-between'
          style={{ background: '#EBF6EB' }}
        >
          <p className='small mb-0' style={{ color: '#5CB85C' }}>Account name</p>
          <p className='small mb-0' style={{ color: '#376E37' }}>{accountName}</p>
        </div>
      )}

      {otpError && (
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
          <ErrorToast message={otpError} />
        </div>
      )}
      </div>

      <div>
      <p
        className='small fw-medium text-center mb-3'
        style={{ color: '#515568', fontSize: '12px', display: 'block' }}
      >
        {/* display:block above overrides a global `p { display: flex }` rule (page.css/styles.css)
            that otherwise stacks each text/span chunk as its own flex item instead of wrapping normally.
            The links below need float:none/display:inline to counter a global `a { float: left }`
            rule (page.css) that would otherwise break inline wrapping the same way. */}
        By clicking &quot;proceed&quot; you agree to our{' '}
        <a
          href={TERMS_AND_CONDITIONS_URL}
          target='_blank'
          rel='noopener noreferrer'
          style={{ float: 'none', display: 'inline', color: '#F2931E' }}
        >
          Terms and Conditions
        </a>{' '}
        and{' '}
        <a
          href={PRIVACY_POLICY_URL}
          target='_blank'
          rel='noopener noreferrer'
          style={{ float: 'none', display: 'inline', color: '#F2931E' }}
        >
          Privacy Policy.
        </a>
      </p>

      <Button
        style={{
          background: !isFormValid ? '#FCE9D2' : '#F26522',
          padding: '12px',
        }}
        className='w-100 rounded-pill text-white border-0'
        onClick={handleProceed}
        disabled={loading || !isFormValid}
      >
        {loading ? 'Processing...' : 'Proceed'}
      </Button>
      </div>
    </div>
  )
}

export default AccountDetailsScreen
